/* Shared UI atoms, ported from prototype/contract-ops.jsx.
   Visual details here are a specification. Change them only deliberately. */

import React, { useRef, useState } from "react";
import { T, SERIF, priorityColor } from "../../theme/tokens.js";
import logoBlack from "../../assets/wittobriens.png";
import logoWhite from "../../assets/wittobriens-white.png";

export const Label = ({ children, color }) => (
  <div style={{
    fontSize: 11, fontWeight: 700, letterSpacing: "0.12em",
    textTransform: "uppercase", color: color || T.gold,
  }}>{children}</div>
);

export const Chip = ({ children, color }) => {
  const c = color || T.soft;
  return (
    <span style={{
      background: c + "1A", color: c, border: `1px solid ${c}66`,
      borderRadius: 3, padding: "1px 8px", fontSize: 11, fontWeight: 700,
      letterSpacing: "0.04em", textTransform: "uppercase", whiteSpace: "nowrap",
    }}>{children}</span>
  );
};

export const PriorityChip = ({ priority }) => (
  <Chip color={priorityColor(priority)}>{priority || "Low"}</Chip>
);

const BTN_KINDS = {
  primary: { background: T.navy, color: "#fff", border: `1px solid ${T.navy}` },
  gold: { background: T.gold, color: "#fff", border: `1px solid ${T.gold}` },
  ghost: { background: "transparent", color: T.navy, border: `1px solid ${T.line}` },
  danger: { background: "transparent", color: T.risk, border: `1px solid ${T.risk}55` },
};

export const Btn = ({ children, onClick, kind = "primary", small, disabled, type = "button", full }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    style={{
      ...BTN_KINDS[kind],
      borderRadius: 4,
      padding: small ? "4px 10px" : "8px 16px",
      fontSize: small ? 12 : 14,
      fontWeight: 600,
      cursor: disabled ? "default" : "pointer",
      opacity: disabled ? 0.5 : 1,
      width: full ? "100%" : undefined,
    }}
  >{children}</button>
);

const inputBase = {
  width: "100%", padding: "8px 10px", border: `1px solid ${T.line}`,
  borderRadius: 4, fontSize: 14, background: "#fff", color: T.ink,
};

export const Field = ({ f, value, onChange, autoFocus, placeholder }) => {
  if (f.type === "select") {
    return (
      <select style={inputBase} value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
        {!f.req && <option value="">{f.placeholder || "Not set"}</option>}
        {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (f.type === "textarea") {
    return (
      <textarea
        style={{ ...inputBase, minHeight: 56, resize: "vertical" }}
        value={value ?? ""}
        placeholder={placeholder || f.placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  return (
    <input
      style={inputBase}
      autoFocus={autoFocus}
      placeholder={placeholder || f.placeholder}
      type={f.type === "number" ? "number" : f.type === "date" ? "date" : f.type === "password" ? "password" : f.type === "email" ? "email" : "text"}
      value={value ?? ""}
      onChange={(e) =>
        onChange(f.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)
      }
    />
  );
};

export const Labelled = ({ label, hint, children }) => (
  <label style={{ fontSize: 13, fontWeight: 600, color: T.soft, display: "block" }}>
    {label}
    <div style={{ marginTop: 4 }}>{children}</div>
    {hint && <div style={{ fontSize: 12, fontWeight: 400, color: T.soft, marginTop: 4 }}>{hint}</div>}
  </label>
);

export const Modal = ({ title, children, onClose, wide }) => (
  <div
    onClick={onClose}
    style={{
      position: "fixed", inset: 0, background: "rgba(20,40,75,0.5)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 50, padding: 16,
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        background: T.panel, borderRadius: 6, borderTop: `4px solid ${T.gold}`,
        width: "100%", maxWidth: wide ? 780 : 540, maxHeight: "88vh",
        overflow: "auto", padding: 24, border: `1px solid ${T.line}`,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: 18, color: T.navy, fontFamily: SERIF }}>{title}</h3>
        <button onClick={onClose} aria-label="Close" style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: T.soft }}>×</button>
      </div>
      {children}
    </div>
  </div>
);

/* A panel is as wide as it is given. The title is often a filename, which is
   one long unbreakable word, and without a floor of zero it would widen the
   panel and then the page. */
export const Panel = ({ title, right, children, span, accent }) => (
  <div style={{
    background: T.panel, border: `1px solid ${T.line}`,
    borderTop: `3px solid ${accent || T.navy}`, borderRadius: 4, padding: 18,
    gridColumn: span ? "1 / -1" : undefined,
    minWidth: 0,
  }}>
    {(title || right) && (
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                    marginBottom: 12, gap: 10, minWidth: 0 }}>
        <div title={typeof title === "string" ? title : undefined} style={{
          fontFamily: SERIF, fontSize: 16.5, fontWeight: 700, color: T.navy,
          minWidth: 0, overflowWrap: "anywhere",
        }}>{title}</div>
        {right && <div style={{ flex: "0 0 auto" }}>{right}</div>}
      </div>
    )}
    {children}
  </div>
);

export const Empty = ({ text }) => (
  <div style={{ padding: 26, textAlign: "center", color: T.soft, fontSize: 13 }}>
    {text || "No data yet"}
  </div>
);

/* A card is as wide as the grid gives it and no wider.
 *
 * minWidth 0 is the whole fix. A grid item is sized to its content by default,
 * so one long unbreakable string, a filename with underscores instead of
 * spaces, pushed this card wider, then the row wider, then the page wider,
 * until the panel ran off the right of the screen. With that floor at zero the
 * card takes what it is given and the text wraps inside it.
 *
 * The line underneath is held to two lines with the whole of it on hover, so a
 * long filename says what it is without taking three lines of a card that is
 * mostly one big number. */
export const Kpi = ({ label, value, sub, color }) => (
  <div style={{
    background: T.panel, border: `1px solid ${T.line}`,
    borderTop: `3px solid ${color || T.navy}`, borderRadius: 4, padding: "14px 16px",
    minWidth: 0, overflow: "hidden",
  }}>
    <Label color={T.navy}>{label}</Label>
    <div style={{
      fontSize: 26, fontWeight: 700, marginTop: 4,
      color: !color || color === T.navy ? T.ink : color,
      overflowWrap: "anywhere",
    }}>{value}</div>
    {sub && (
      <div title={typeof sub === "string" ? sub : undefined} style={{
        fontSize: 12, color: T.soft, marginTop: 2,
        display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
        overflow: "hidden", overflowWrap: "anywhere",
      }}>{sub}</div>
    )}
  </div>
);

export const Notice = ({ kind = "info", children }) => {
  const color = kind === "error" ? T.risk : kind === "ok" ? T.ok : kind === "warn" ? T.warn : T.steel;
  return (
    <div style={{
      fontSize: 13, color, background: color + "12",
      border: `1px solid ${color}44`, borderRadius: 4, padding: "9px 12px",
    }}>{children}</div>
  );
};

export const UploadZone = ({ onFiles, busy, status, accept, prompt, hint }) => {
  const [drag, setDrag] = useState(false);
  const inputRef = useRef();
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.nativeEvent?.stopImmediatePropagation) e.nativeEvent.stopImmediatePropagation();
        setDrag(false);
        if (!busy) onFiles([...e.dataTransfer.files]);
      }}
      onClick={() => !busy && inputRef.current?.click()}
      style={{
        border: `2px dashed ${drag ? T.gold : T.line}`,
        background: drag ? T.chip : "transparent",
        borderRadius: 6, padding: 28, textAlign: "center",
        cursor: busy ? "default" : "pointer",
      }}
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={accept || ".pdf,.docx,.txt,.md"}
        style={{ display: "none" }}
        onChange={(e) => {
          const files = [...e.target.files];
          e.target.value = "";
          if (files.length && !busy) onFiles(files);
        }}
      />
      {busy ? (
        <div style={{ color: T.navy, fontWeight: 700 }}>{status}</div>
      ) : (
        <>
          <div style={{ fontSize: 15, fontWeight: 700, color: T.navy, marginBottom: 4 }}>
            {prompt || "Drop contract documents here"}
          </div>
          <div style={{ fontSize: 13, color: T.soft }}>
            {hint || "PDF, Word (.docx), or text. Multiple documents welcome. Click to browse."}
          </div>
        </>
      )}
    </div>
  );
};

/* The serif plate that identifies the workspace. Used on the sidebar, the
   sign-in card and the contract picker so all three read as one system. */
/* The real logo, not a typeset stand-in.
 *
 * Two renditions of one piece of artwork, both trimmed to the ink so the
 * width on screen is the width of the mark and there is no invisible margin
 * deciding the spacing. Black for the cream panels, white for the navy
 * sidebar. The artwork is a single colour on transparency, so reversing it is
 * the ordinary one-colour treatment rather than a redraw.
 *
 * Drawn at 1000px wide and shown at 212, which fills the sidebar rail and is
 * what makes the Ambipar line under it readable. The rail was widened to 240
 * for exactly this reason: that line is a fixed fraction of the lockup, so it
 * cannot be made clearer by a better file, only by a wider one. Below roughly
 * 150px wide it stops being readable at all, so anywhere narrower than that
 * wants the wordmark alone rather than a shrunken pair.
 */
export const Wordmark = ({ onLight, width = 212 }) => (
  <img
    src={onLight ? logoBlack : logoWhite}
    alt="Witt O'Brien's, part of the Ambipar Group"
    width={width}
    style={{ width, height: "auto", display: "block" }}
  />
);
